import { ladderRulesOf, buildLadder, rungByKey, rungOf, rungKey, sevenLevelRank, } from './ladder.js';
export function auctionRulesOf(config) {
    return {
        ...ladderRulesOf(config),
        misereAvailability: config.misereAvailability,
        openMisereAvailability: config.openMisereAvailability,
        bidAfterPass: config.bidAfterPass,
        raiseOwnBid: config.raiseOwnBid,
    };
}
export function ladderForAuction(state) {
    return buildLadder(state.rules);
}
/** Under the default "pass is final", a seat that has passed never acts again.
 *  Under the 'always' re-entry option nobody is ever permanently out. */
function outOfAuction(state, seat) {
    if (state.rules.bidAfterPass === 'always')
        return false;
    return state.passed[seat] === true;
}
function nextBidder(state, from) {
    for (let i = 1; i <= state.seatCount; i++) {
        const s = (from + i) % state.seatCount;
        if (!outOfAuction(state, s))
            return s;
    }
    return from;
}
function isClosed(state) {
    const n = state.seatCount;
    if (state.rules.bidAfterPass === 'always') {
        // Re-entry allowed: the auction ends only on a run of consecutive passes.
        return state.high !== null
            ? state.consecutivePasses >= n - 1
            : state.consecutivePasses >= n;
    }
    // Pass is final: the auction ends when everyone except the high bidder is out.
    const passedCount = state.passed.filter(Boolean).length;
    return state.high !== null ? passedCount >= n - 1 : passedCount >= n;
}
/** Does this rung's availability option let it be bid right now? */
function rungAvailable(state, rung) {
    const { rules } = state;
    if (rung.category !== 'misere' && rung.category !== 'openMisere')
        return true;
    const availability = rung.category === 'misere' ? rules.misereAvailability : rules.openMisereAvailability;
    if (availability === 'off')
        return false;
    if (availability === 'anytime')
        return true;
    // 'after7': someone must already have bid at the 7 level or above.
    const ladder = ladderForAuction(state);
    return state.high !== null && state.high.rank >= sevenLevelRank(ladder);
}
export function fhContractFromRung(rung, contractor) {
    const spec = rung.spec;
    const mode = spec.kind === 'misere' ? 'misere'
        : spec.kind === 'openMisere' ? 'openMisere'
            : spec.strain === 'NT' ? 'nt' : 'suit';
    return {
        spec,
        rank: rung.rank,
        value: rung.value,
        tricks: rung.tricks,
        mode,
        trumpSuit: mode === 'suit' && spec.kind === 'suit' && spec.strain !== 'NT' ? spec.strain : null,
        contractor,
        label: rung.label,
    };
}
export const fhAuction = {
    init({ config, table, dealer }) {
        return {
            rules: auctionRulesOf(config),
            seatCount: table.seatCount,
            dealer,
            calls: [],
            passed: Array.from({ length: table.seatCount }, () => false),
            consecutivePasses: 0,
            high: null,
            // Rules §3.1.1: the player to the dealer's left bids first.
            toAct: (dealer + 1) % table.seatCount,
            closed: false,
        };
    },
    legalCalls(state, seat) {
        if (state.closed || seat !== state.toAct || outOfAuction(state, seat))
            return [];
        const calls = [{ type: 'pass' }];
        // Rules §3.1.5: a standing high bidder may not bid over themselves by
        // default — the only way up is somebody else outbidding them first.
        const mayBid = state.high === null || state.high.seat !== seat || state.rules.raiseOwnBid;
        if (mayBid) {
            const floor = state.high?.rank ?? 0;
            for (const rung of ladderForAuction(state)) {
                if (rung.rank <= floor)
                    continue;
                if (!rungAvailable(state, rung))
                    continue;
                calls.push({ type: 'bid', spec: rung.spec });
            }
        }
        return calls;
    },
    apply(state, seat, call) {
        const calls = [...state.calls, { seat, call }];
        if (call.type === 'pass') {
            const passed = state.passed.map((p, i) => (i === seat ? true : p));
            const next = {
                ...state,
                calls,
                passed,
                consecutivePasses: state.consecutivePasses + 1,
            };
            const closed = isClosed(next);
            return { ...next, closed, toAct: closed ? next.toAct : nextBidder(next, seat) };
        }
        const ladder = ladderForAuction(state);
        const rung = rungOf(ladder, call.spec);
        if (!rung)
            return state; // unreachable via legalCalls; a no-op beats a throw
        const previousHigh = state.high;
        const strainChanged = previousHigh !== null &&
            rungByKey(ladder, previousHigh.key)?.spec.kind === 'suit' &&
            rung.spec.kind === 'suit' &&
            (rungByKey(ladder, previousHigh.key)?.spec).strain !== rung.spec.strain;
        // 'ifBidChanged': passed players come back in when the high bid's suit moves.
        const passed = state.rules.bidAfterPass === 'ifBidChanged' && (previousHigh === null || strainChanged)
            ? state.passed.map(() => false)
            : state.passed;
        const next = {
            ...state,
            calls,
            passed,
            consecutivePasses: 0,
            high: { seat, rank: rung.rank, key: rung.key },
        };
        const closed = isClosed(next);
        return { ...next, closed, toAct: closed ? next.toAct : nextBidder(next, seat) };
    },
    status(state) {
        if (!state.closed)
            return { kind: 'open', toAct: state.toAct };
        if (state.high === null)
            return { kind: 'passedOut' }; // rules §3.1.6 / edge #14
        const rung = rungByKey(ladderForAuction(state), state.high.key);
        if (!rung)
            return { kind: 'passedOut' };
        return { kind: 'contract', contract: fhContractFromRung(rung, state.high.seat) };
    },
    callKey(call) {
        return call.type === 'pass' ? 'pass' : `bid:${rungKey(call.spec)}`;
    },
    describe(call, config) {
        if (call.type === 'pass') {
            return {
                label: 'Pass',
                explain: config.bidAfterPass === 'never'
                    ? 'Pass. Passing is final — you cannot bid again this hand.'
                    : 'Pass. You may bid again later this hand.',
            };
        }
        const ladder = buildLadder(ladderRulesOf(config));
        const rung = rungOf(ladder, call.spec);
        if (!rung)
            return { label: '?', explain: 'Unknown bid' };
        const pm = `+${rung.value} / −${rung.value}`;
        if (rung.category === 'misere') {
            return { label: rung.label, explain: `Lose every trick, alone — your partner sits out. ${pm}`, value: rung.value };
        }
        if (rung.category === 'openMisere') {
            return {
                label: rung.label,
                explain: `Lose every trick, alone and with your hand face up from the first lead. ${pm}`,
                value: rung.value,
            };
        }
        const spec = rung.spec;
        const strain = spec.strain === 'NT' ? 'no trumps' : `${rung.label.slice(String(spec.level).length)} trumps`;
        return {
            label: rung.label,
            explain: `Win ${spec.level} of 10 tricks with ${strain}. ${pm}`,
            value: rung.value,
        };
    },
};
//# sourceMappingURL=auction.js.map